import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@lib/api/client';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { useAuthStore } from '@features/auth/store/authStore';
import { CTA, METRIC_LABEL } from '@components/ui/designTokens';
import { GuidedTour, type GuidedStep } from './GuidedTour';
import { captureScreenMap, type ScreenElement } from '../lib/captureScreenMap';
import { visibleScreens } from '../lib/screens';
import { useShowFeedbackToast, useSupportConfig } from '../SupportErrorProvider';
import { useDraggable } from '../lib/useDraggable';
import { Markdown } from './Markdown';

interface SupportResult {
  reply: string;
  session_id: string;
  interaction_id?: string;
  steps?: GuidedStep[];
}

// Un turno del chat. `steps` (si vienen) habilitan el botón "Guíame en pantalla".
interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  steps?: GuidedStep[];
  error?: boolean;
}

// Tareas frecuentes ("guided choice first, free text second"): el cliente arranca
// de un ejemplo en vez de una hoja en blanco. Al tocar uno, se envía directo.
const QUICK_PROMPTS = [
  'Quiero crear un cliente',
  'Quiero emitir una factura',
  'Quiero agregar un producto',
  '¿Por qué me falló una factura?',
];

/** Ancho del sidebar fijo del portal (AppLayout: `lg:w-[260px]`). */
const SIDEBAR_W = 260;

/**
 * Baldosa de marca del asistente. Es el mismo degradado teal → púrpura que usa
 * la cabecera de los modales del sistema (MODAL_ICON_TILE en designTokens), así
 * el asistente se lee como parte del portal y no como un widget pegado.
 */
function BotAvatar({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const box = size === 'lg' ? 'h-14 w-14 rounded-[16px]' : size === 'sm' ? 'h-7 w-7 rounded-lg' : 'h-9 w-9 rounded-[11px]';
  const icon = size === 'lg' ? 'h-7 w-7' : size === 'sm' ? 'h-4 w-4' : 'h-[18px] w-[18px]';
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center bg-gradient-to-br from-teal-500 to-[#6b239e] text-white ${box}`}
      aria-hidden="true"
    >
      <svg className={icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="8" width="16" height="11" rx="4" />
        <circle cx="9.5" cy="13.5" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="14.5" cy="13.5" r="1.15" fill="currentColor" stroke="none" />
        <path d="M12 4.8V8" />
        <circle cx="12" cy="3.6" r="1.3" />
      </svg>
    </span>
  );
}

/** Tres puntitos animados: "el asistente está escribiendo". */
function TypingDots() {
  return (
    <span className="flex items-center gap-1">
      {[0, 150, 300].map((d) => (
        <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-secondary-400" style={{ animationDelay: `${d}ms` }} />
      ))}
    </span>
  );
}

/**
 * Asistente de soporte como CHAT: botón flotante que abre una ventana de
 * conversación (hilo de mensajes, indicador de escritura, composer). Cada
 * respuesta puede traer pasos y ofrecer "Guíame en pantalla" (recorrido guiado).
 * Al abrir se captura la pantalla ANTES del modal para no fotografiarlo.
 *
 * El vestido es el del sistema (Claude Design): la ventana usa la anatomía de
 * los modales del portal —cabecera con baldosa de icono en degradado y kicker
 * mono, cuerpo propio con scroll, pie gris con el composer—, navy `ink-850`
 * como acción primaria y teal `#12c4b2` como acento de marca.
 */
export function SupportReportButton() {
  const { selectedCompany } = useCompanies();
  // Tenant activo: sin el, en el panel del agente no se distingue de que
  // instalacion vino el caso (ver useSupportHelp).
  const tenantInfo = useAuthStore((s) => s.tenantInfo);
  // Rol del usuario: acota el catálogo de pantallas que ve el agente (mismo criterio
  // que el menú lateral, para no guiar a una pantalla que este cliente no tiene).
  const userRole = useAuthStore((s) => s.user?.role);
  const showFeedbackToast = useShowFeedbackToast();
  const { assistantEnabled, guideEnabled } = useSupportConfig();
  const [open, setOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [screenMap, setScreenMap] = useState<ScreenElement[]>([]);
  const [lastMessage, setLastMessage] = useState('');
  const [tour, setTour] = useState<{ steps: GuidedStep[]; sessionId?: string } | null>(null);
  // La captura en curso. El panel abre sin esperarla, pero si el cliente escribe
  // rápido el primer mensaje la esperamos un momento antes de mandar: es el turno
  // donde la imagen más aporta (el agente ve la pantalla del problema).
  const shotRef = useRef<Promise<void> | null>(null);
  const screenshotRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const msgId = useRef(0);
  const nextId = () => (msgId.current += 1);

  // El composer crece con el texto (hasta un tope) y vuelve a una línea al vaciarse.
  function autosize() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 128) + 'px';
  }

  // Botón flotante arrastrable; arranca abajo-IZQUIERDA y recuerda dónde lo dejes.
  //
  // OJO con la x: este portal tiene un sidebar navy fijo de 260px (`lg:w-[260px]`
  // en AppLayout, 80px colapsado). En x=20 el botón queda DEBAJO del sidebar y,
  // siendo navy sobre navy, es invisible. Así que en escritorio arranca justo a
  // la derecha del sidebar; en móvil el sidebar es off-canvas y x=20 está bien.
  const { pos, dragHandlers, wasDragged } = useDraggable('fe-support-help-btn', (vw, vh) => ({
    x: vw >= 1024 ? SIDEBAR_W + 20 : 20,
    y: vh - 72,
  }));
  // La ventana del chat también es arrastrable (por su encabezado). Arranca
  // abajo-DERECHA y recuerda dónde la dejes.
  const PANEL_W = 372;
  const PANEL_H = 560;
  const panelDrag = useDraggable(
    'fe-support-panel',
    (vw, vh) => ({ x: vw - PANEL_W - 20, y: vh - PANEL_H - 20 }),
    { w: PANEL_W, h: PANEL_H }
  );
  // En pantallas chicas el chat es un bottom-sheet a lo ancho (no una tarjeta
  // flotante/arrastrable): más cómodo en móvil y no se sale de pantalla.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  // Cartel "se puede mover" la PRIMERA vez (se recuerda que ya se vio).
  const [showMoveHint, setShowMoveHint] = useState(() => {
    try { return !localStorage.getItem('fe-support-help-hint'); } catch { return false; }
  });
  const dismissMoveHint = () => {
    setShowMoveHint(false);
    try { localStorage.setItem('fe-support-help-hint', '1'); } catch { /* ignore */ }
  };
  // Auto-ocultar el cartel a los 9s.
  useEffect(() => {
    if (!showMoveHint) return;
    const t = setTimeout(dismissMoveHint, 9000);
    return () => clearTimeout(t);
  }, [showMoveHint]);

  // Aviso "el chat se puede mover" — la PRIMERA vez que se abre (se recuerda).
  const [showPanelHint, setShowPanelHint] = useState(false);
  const dismissPanelHint = () => {
    setShowPanelHint(false);
    try { localStorage.setItem('fe-support-panel-hint', '1'); } catch { /* ignore */ }
  };
  useEffect(() => {
    if (!open || isMobile) return; // en móvil no se arrastra: no mostramos el aviso
    // Solo con el hilo vacío: el cartel flota sobre el inicio de la conversación,
    // así que si ya hay mensajes taparía el primero.
    if (messages.length > 0) return;
    try { if (localStorage.getItem('fe-support-panel-hint')) return; } catch { return; }
    setShowPanelHint(true);
    const t = setTimeout(dismissPanelHint, 7000);
    return () => clearTimeout(t);
  }, [open, isMobile, messages.length]);

  // Al primer mensaje, el cartel se va (ya no hay lugar libre donde mostrarlo).
  useEffect(() => {
    if (messages.length > 0 && showPanelHint) dismissPanelHint();
  }, [messages.length, showPanelHint]);

  // Permite abrir el asistente desde cualquier parte del portal disparando
  // `window.dispatchEvent(new CustomEvent('fe:open-support'))`.
  useEffect(() => {
    const onOpen = () => { if (assistantEnabled && !open) openReporter(); };
    window.addEventListener('fe:open-support', onOpen);
    return () => window.removeEventListener('fe:open-support', onOpen);
  }, [assistantEnabled, open]);

  // Cerrar con Escape (no hay fondo oscuro donde hacer clic afuera).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Auto-scroll al último mensaje / al indicador de escritura.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, sending, open]);

  // El mapa de la pantalla en este instante. `captureScreenMap` excluye la UI del
  // asistente, así que es válido incluso con el panel abierto. Si algo falla, se cae
  // al mapa de la apertura antes que perder el contexto.
  function freshScreenMap(): ScreenElement[] {
    try {
      const map = captureScreenMap();
      return map.length ? map : screenMap;
    } catch {
      return screenMap;
    }
  }

  function openReporter() {
    // El panel abre YA. Antes esperaba a html2canvas sobre todo el body (1-3 s en
    // pantallas cargadas) con el botón en spinner: el cliente pedía ayuda y lo
    // primero que veía era una espera. La captura sigue corriendo, por detrás, y se
    // adjunta si llegó a tiempo al primer envío.
    setScreenMap(captureScreenMap());
    setOpen(true);
    setCapturing(true);
    shotRef.current = captureShot();
  }

  /** Foto de la pantalla, para que el agente vea el problema. Corre en paralelo. */
  async function captureShot() {
    try {
      const { default: html2canvas } = await import('html2canvas');
      // Escala 1 para no inflar el tamaño; el agente acepta máx 5 MB por imagen.
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        scale: 1,
        // Excluye la UI del asistente: como ahora el panel ya está abierto cuando
        // corre la captura, sin esto el cliente le mandaría al agente una foto del
        // chat en vez de su pantalla.
        ignoreElements: (el) => el instanceof HTMLElement && !!el.dataset?.supportUi,
      });
      // JPEG con calidad adaptativa: bajamos la calidad hasta quedar < 4.5 MB.
      const LIMIT = 4.5 * 1024 * 1024;
      let dataUrl = '';
      for (const q of [0.7, 0.5, 0.35, 0.25]) {
        dataUrl = canvas.toDataURL('image/jpeg', q);
        if (dataUrl.length * 0.75 < LIMIT) break;
      }
      screenshotRef.current = dataUrl;
      setScreenshot(dataUrl);
    } catch {
      screenshotRef.current = null;
      setScreenshot(null); // si falla la captura, igual se puede chatear solo con texto
    } finally {
      setCapturing(false);
    }
  }

  function close() {
    // Cerramos la ventana pero conservamos el hilo: al reabrir, la charla sigue.
    setOpen(false);
  }

  function resetChat() {
    setMessages([]);
    setSessionId(undefined);
    // Hilo nuevo = primer turno de nuevo, así que se vuelve a sacar la foto: la de
    // la apertura puede ser de otra pantalla si el cliente ya se movió.
    screenshotRef.current = null;
    setScreenshot(null);
    setScreenMap(captureScreenMap());
    shotRef.current = captureShot();
    setMessage('');
    requestAnimationFrame(autosize);
  }

  async function send(textArg?: string) {
    const text = (textArg ?? message).trim();
    if (!text || sending) return;
    setLastMessage(text); // objetivo para la guía iterativa
    setMessage('');
    requestAnimationFrame(autosize);
    setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
    setSending(true);
    try {
      // La captura se manda SOLO en el primer mensaje del hilo. Antes iba en cada
      // turno (nunca se limpiaba): ~0.5-2 MB de base64 subiendo de nuevo por mensaje
      // y una imagen más para analizar en cada inferencia, sin aportar nada nuevo
      // — el agente ya la vio y la conversación sigue en la misma sesión.
      const firstTurn = !sessionId;
      let shot = screenshot;
      if (firstTurn && !shot && shotRef.current) {
        // Todavía se está sacando: le damos un margen corto y seguimos sin ella si
        // no llega. Nunca al revés — no vale la pena hacer esperar al cliente.
        await Promise.race([shotRef.current, new Promise((r) => setTimeout(r, 1200))]);
        shot = screenshotRef.current;
      }
      const images =
        firstTurn && shot
          ? [{ mime_type: 'image/jpeg', data_base64: shot.split(',', 2)[1] }]
          : [];
      const { data } = await apiClient.post<SupportResult>(
        '/support/report',
        {
          message: text,
          sessionId,
          context: {
            url: window.location.href,
            path: window.location.pathname,
            user_agent: navigator.userAgent,
            // Mapa de la pantalla. Se re-captura en cada envío (es solo lectura del
            // DOM, milisegundos): si el cliente navegó desde que abrió el panel, el
            // mapa de la apertura ya no describe lo que tiene delante y el agente
            // guiaba sobre elementos que no están.
            screen_elements: freshScreenMap(),
            // Catálogo de pantallas: el agente solo puede navegar a estas rutas.
            // Filtrado por el rol del usuario — si el cliente no ve Reportes en su
            // menú, el agente no debe poder proponerle "ve a Reportes".
            screens: visibleScreens(userRole),
            // Identidad de la compañía → queda guardada con el reporte.
            company_name: selectedCompany?.name || selectedCompany?.fullName || undefined,
            company_legal_name: selectedCompany?.fullName || undefined,
            company_ruc: selectedCompany?.ruc || selectedCompany?.numberId || undefined,
            company_dv: selectedCompany?.dv || undefined,
            company_id: selectedCompany?.id || undefined,
            tenant_id: tenantInfo?.id || undefined,
            tenant_identifier: tenantInfo?.identifier || undefined,
            tenant_name: tenantInfo?.name || undefined,
          },
          images,
        },
        // El agente puede tardar (análisis de imagen + búsqueda + pasos guiados);
        // damos margen por encima del timeout del backend (120s).
        { timeout: 130000 }
      );
      setMessages((m) => [...m, { id: nextId(), role: 'assistant', text: data.reply, steps: data.steps }]);
      setSessionId(data.session_id);
      if (data.interaction_id) showFeedbackToast(data.interaction_id, text.slice(0, 40));
    } catch (e: any) {
      const detail = e?.response?.data?.message;
      setMessages((m) => [
        ...m,
        { id: nextId(), role: 'assistant', error: true, text: detail || 'No pude procesar tu mensaje. Intenta de nuevo en un momento.' },
      ]);
    } finally {
      setSending(false);
    }
  }

  // Arranca el recorrido guiado con los pasos de una respuesta puntual.
  function startTour(steps: GuidedStep[]) {
    setOpen(false);
    setTour({ steps, sessionId });
  }

  // Si el asistente está apagado para este proyecto, no mostramos nada (portal normal).
  if (!assistantEnabled) return null;

  const empty = messages.length === 0 && !sending;

  return (
    <>
      {/* Cartel "se puede mover" — solo la primera vez. Aparece arriba del botón. */}
      {showMoveHint && (
        <div
          data-support-ui
          style={{ left: pos.x, top: Math.max(8, pos.y - 52), maxWidth: 268 }}
          className="fixed z-[55] flex animate-support-in items-center gap-2 rounded-xl bg-ink-850 px-3 py-2 text-[12px] font-medium text-white shadow-[0_18px_40px_-16px_rgba(10,34,51,.5)]"
        >
          <span>✋ Arrástrame para moverme a donde quieras</span>
          <button onClick={dismissMoveHint} aria-label="Entendido" className="shrink-0 rounded-md px-1 text-white/60 transition-colors hover:text-white">✕</button>
        </div>
      )}

      {/* Botón flotante: navy del sistema con latido teal. Alterna abrir/cerrar el
          chat y es arrastrable; cuando el chat está abierto se convierte en una X. */}
      <button
        type="button"
        onClick={() => { if (!wasDragged()) { if (open) close(); else openReporter(); } }}
        title={open ? 'Cerrar chat de ayuda' : '¿Necesitas ayuda? Chatea con el asistente — arrástrame para moverme'}
        aria-label={open ? 'Cerrar chat de ayuda' : 'Abrir chat de ayuda'}
        data-support-ui
        {...dragHandlers}
        onPointerDown={(e) => { dismissMoveHint(); dragHandlers.onPointerDown(e); }}
        style={{ left: pos.x, top: pos.y, touchAction: 'none' }}
        className={`fixed z-[61] flex h-14 w-14 cursor-grab items-center justify-center rounded-full bg-ink-850 text-white shadow-[0_18px_40px_-16px_rgba(10,34,51,.55)] transition-[transform,background-color] hover:scale-105 hover:bg-[#142d40] active:cursor-grabbing disabled:opacity-70 motion-reduce:animate-none ${open ? '' : 'animate-help-pulse'}`}
      >
        {/* El estado "abierto" manda sobre "capturando": la captura ahora corre por
            detrás con el panel ya abierto, así que un spinner acá sería un spinner
            permanente en vez de la X de cerrar. */}
        {open ? (
          <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
            <path d="M8 10h.01M12 10h.01M16 10h.01" />
          </svg>
        )}
      </button>

      {/* Ventana de chat — panel FLOTANTE y ARRASTRABLE (no modal centrado): no
          oscurece la pantalla ni tapa el contenido, así se puede seguir trabajando
          y ver el recorrido guiado. Se mueve arrastrando su encabezado. */}
      {open && (
        <div
          data-support-ui
          style={isMobile ? undefined : { left: panelDrag.pos.x, top: panelDrag.pos.y, width: PANEL_W, height: PANEL_H }}
          className={
            isMobile
              ? 'fixed inset-x-0 bottom-0 z-[60] flex h-[88vh] max-h-[88vh] w-full animate-support-sheet-in flex-col overflow-hidden rounded-t-[20px] bg-white font-display shadow-[0_-30px_80px_-20px_rgba(10,34,51,.4)] dark:bg-secondary-800'
              : 'fixed z-[60] flex max-h-[calc(100vh-16px)] max-w-[calc(100vw-16px)] animate-support-in flex-col overflow-hidden rounded-[20px] bg-white font-display shadow-[0_40px_100px_-20px_rgba(10,34,51,.4)] dark:bg-secondary-800'
          }
        >
          {/* Aviso de una sola vez: el chat se puede mover (solo escritorio). El
              texto es corto a propósito: el panel tiene overflow-hidden, así que
              un cartel más ancho que los 372px se ve cortado contra el borde. */}
          {showPanelHint && !isMobile && (
            <div className="pointer-events-none absolute left-1/2 top-16 z-10 flex max-w-[calc(100%-24px)] -translate-x-1/2 animate-support-in items-center gap-1.5 rounded-full bg-ink-850/95 px-3 py-1.5 text-[11px] font-medium text-white shadow-lg">
              <span aria-hidden="true">✋</span> Arrastra desde aquí para moverme
            </div>
          )}

          {/* Cabecera = asa para mover el panel. Misma anatomía que la cabecera de
              los modales del sistema: baldosa de icono en degradado, kicker mono y
              título, cerrada con la regla dasheada. */}
          <div
            {...(isMobile ? {} : panelDrag.dragHandlers)}
            onPointerDown={isMobile ? undefined : (e) => { dismissPanelHint(); panelDrag.dragHandlers.onPointerDown(e); }}
            style={isMobile ? undefined : { touchAction: 'none' }}
            title={isMobile ? undefined : 'Arrástrame para mover el chat'}
            className={`relative flex items-center gap-3 border-b border-dashed border-[#eef0f2] px-4 pb-[14px] pt-[18px] dark:border-secondary-700 ${isMobile ? '' : 'cursor-grab active:cursor-grabbing'}`}
          >
            {/* Asa de arrastre (afordancia visual de "se puede mover") */}
            <span className="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-full bg-secondary-200 dark:bg-secondary-600" aria-hidden="true" />
            <BotAvatar />
            <div className="min-w-0 flex-1">
              <p className={METRIC_LABEL}>ASISTENTE</p>
              <h2 className="truncate text-[15px] font-extrabold leading-tight tracking-[-0.02em] text-ink-850 dark:text-white">
                Soporte del portal
              </h2>
            </div>
            <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-secondary-500">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-500" /> En línea
            </span>
            {messages.length > 0 && (
              <button
                onClick={resetChat}
                onPointerDown={(e) => e.stopPropagation()}
                title="Nueva conversación"
                aria-label="Nueva conversación"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-transparent text-secondary-400 transition-colors hover:border-[#ececec] hover:bg-[#f6f6f7] hover:text-ink-850 dark:hover:border-secondary-600 dark:hover:bg-secondary-700 dark:hover:text-white"
              >
                <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
              </button>
            )}
            <button
              onClick={close}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="Cerrar"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#ececec] bg-[#fafafa] text-secondary-500 transition-colors hover:border-ink-850 hover:bg-ink-850 hover:text-white dark:border-secondary-600 dark:bg-secondary-900 dark:text-secondary-300"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Hilo de mensajes */}
          <div ref={scrollRef} className="flex-1 space-y-3.5 overflow-y-auto bg-[#fbfbfc] px-4 py-4 dark:bg-secondary-900">
            {empty ? (
              /* Estado inicial: saludo con la baldosa de marca y tareas frecuentes. */
              <div className="flex h-full flex-col items-center justify-center gap-5 px-2 text-center">
                <BotAvatar size="lg" />
                <div>
                  <p className="text-[19px] font-extrabold tracking-[-0.02em] text-ink-850 dark:text-white">
                    ¡Hola! ¿Con qué te ayudo?
                  </p>
                  <p className="mt-1 text-[13px] leading-[1.55] text-secondary-500">
                    Pregúntame lo que necesites o empieza con una de estas:
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {QUICK_PROMPTS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => send(q)}
                      className="rounded-full border border-[#ececec] bg-white px-3 py-1.5 text-[12.5px] font-medium text-ink-850 transition-colors hover:border-teal-500/40 hover:bg-teal-500/[.06] dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((m) =>
                  m.role === 'user' ? (
                    <div key={m.id} className="flex animate-support-in justify-end">
                      <div className="max-w-[84%] whitespace-pre-wrap break-words rounded-[14px] rounded-br-[6px] bg-ink-850 px-3.5 py-2 text-[13.5px] font-medium leading-[1.5] text-white">
                        {m.text}
                      </div>
                    </div>
                  ) : (
                    <div key={m.id} className="flex animate-support-in items-start gap-2">
                      <BotAvatar size="sm" />
                      <div className="min-w-0 max-w-[84%] space-y-2">
                        <div
                          className={`rounded-[14px] rounded-bl-[6px] border px-3.5 py-2 text-[13.5px] leading-[1.55] ${
                            m.error
                              ? 'border-danger-200 bg-danger-50 text-danger-700 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-200'
                              : 'border-[#ececec] bg-white text-ink-850 dark:border-secondary-700 dark:bg-secondary-800 dark:text-secondary-100'
                          }`}
                        >
                          <Markdown text={m.text} />
                        </div>
                        {m.steps && m.steps.length > 0 && (
                          <div className="rounded-xl border border-[#eef0f2] bg-white p-3 dark:border-secondary-700 dark:bg-secondary-800">
                            <p className={`mb-1.5 ${METRIC_LABEL}`}>PASOS SUGERIDOS</p>
                            <ol className="list-decimal space-y-1 pl-5 text-[13px] leading-[1.5] text-secondary-600 marker:font-semibold marker:text-teal-600 dark:text-secondary-300">
                              {m.steps.map((s, i) => (
                                <li key={i}>
                                  {/* Via Markdown: el agente escribe los nombres de botones y
                                      pantallas en **negrita** y sin esto se veian los asteriscos
                                      crudos ("Toca **Nuevo Cliente**"). */}
                                  <Markdown text={s.label || `${s.action} ${s.target ?? ''}`} className="inline" />
                                </li>
                              ))}
                            </ol>
                            {guideEnabled && (
                              <button onClick={() => startTour(m.steps!)} className={`${CTA} mt-2.5 w-full justify-center`}>
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="3 11 22 2 13 21 11 13 3 11" />
                                </svg>
                                Guíame en pantalla
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}

                {sending && (
                  <div className="flex animate-support-in items-start gap-2">
                    <BotAvatar size="sm" />
                    <div className="rounded-[14px] rounded-bl-[6px] border border-[#ececec] bg-white px-4 py-3 dark:border-secondary-700 dark:bg-secondary-800">
                      <TypingDots />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Composer anclado abajo, sobre la banda gris del pie de los modales. */}
          <div className="border-t border-[#f0f0f0] bg-[#fafafa] p-3 dark:border-secondary-700 dark:bg-secondary-900/40">
            <div className="flex items-end gap-2 rounded-[12px] border border-[#ececec] bg-white px-2.5 py-1.5 transition-colors focus-within:border-teal-500/60 focus-within:ring-[3px] focus-within:ring-teal-500/[.14] dark:border-secondary-600 dark:bg-secondary-800">
              <textarea
                ref={textareaRef}
                value={message}
                rows={1}
                onChange={(e) => { setMessage(e.target.value); autosize(); }}
                onKeyDown={(e) => {
                  // Enter envía; Shift+Enter (o ⌘/Ctrl+Enter) hace salto de línea.
                  if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                autoFocus
                placeholder="Escribe tu mensaje…"
                /* border/ring en 0: el foco lo dibuja el contenedor (anillo teal del
                   sistema). Sin esto, @tailwindcss/forms le pone su anillo azul por
                   defecto y quedaban dos focos encimados, uno azul y otro teal. */
                className="max-h-32 flex-1 resize-none border-0 bg-transparent px-1.5 py-1.5 text-[13.5px] font-medium leading-[1.5] text-ink-850 outline-none focus:border-0 focus:ring-0 placeholder:font-normal placeholder:text-secondary-400 dark:text-white"
              />
              <button
                onClick={() => send()}
                disabled={!message.trim() || sending}
                aria-label="Enviar"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-ink-850 text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" />
                </svg>
              </button>
            </div>
            <p className="mt-1.5 text-center text-[11px] text-secondary-400">
              {/* Mientras la captura se saca por detrás, se avisa acá en vez de
                  hacer esperar la apertura del panel. */}
              {capturing && !sessionId
                ? 'Preparando la captura de tu pantalla… ya puedes escribir'
                : 'Enter para enviar · Shift+Enter para salto de línea'}
            </p>
          </div>
        </div>
      )}

      {/* Recorrido guiado en pantalla a partir de los pasos de una respuesta */}
      {tour && tour.steps.length > 0 && (
        <GuidedTour
          steps={tour.steps}
          sessionId={tour.sessionId}
          goal={lastMessage}
          userRole={userRole}
          onComplete={(journey) =>
            apiClient
              .post('/support/guide-complete', { sessionId: tour.sessionId, goal: lastMessage, journey })
              .catch(() => {})
          }
          onClose={() => setTour(null)}
        />
      )}
    </>
  );
}
